// src/components/CheckoutAccountPanel.tsx
// Inline sign-in / account creation at the top of the checkout Contact step.
//
// There are no passwords on this site — an account IS an OTP-verified session
// (customer-send-otp → customer-verify-otp → 30-day JWT), and customer-send-otp
// already creates the Square customer record for an unknown email. So "create
// an account" and "sign in" are the same three fields, and this panel doesn't
// distinguish between them: the buyer types their email, types the code, and is
// signed in either way.
//
// Why it earns its place in checkout, given it costs a trip to the inbox:
//   - Signing in prefills the address and reveals the cards on file (the whole
//     payoff of the saved-card work), so a returning buyer's next checkout is
//     land → Pay.
//   - Only a signed-in buyer can tick "Save my information for a faster
//     checkout" at the payment step — a card on file hangs off a customer
//     profile, so a guest has nothing to hang it on.
// It is entirely optional and starts collapsed: guest checkout must not grow a
// login wall, so nothing here blocks Save & Continue.

import React, { useEffect, useState } from "react";
import {
  getCustomerProfile,
  sendCustomerOtp,
  SavedAddress,
  verifyCustomerOtp,
} from "../api/customer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";

export interface SignedInDetails {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address?: SavedAddress;
}

type Props = {
  /** The buyer email typed into the contact form, used as the default here. */
  email: string;
  /** Called once the session exists, with whatever Square holds for the
   *  account. The caller decides what to prefill — it must not clobber fields
   *  the buyer has already filled in. */
  onSignedIn: (details: SignedInDetails) => void;
  /** Called after signing out, so the caller can drop the contact details this
   *  panel's sign-in put there. The button says "Not you?" — see the handler. */
  onSignedOut?: () => void;
};

type Stage = "collapsed" | "email" | "otp";

const CheckoutAccountPanel: React.FC<Props> = ({
  email,
  onSignedIn,
  onSignedOut,
}) => {
  const { session, login, logout, refreshSession } = useCustomerAuth();

  const [stage, setStage] = useState<Stage>("collapsed");
  const [accountEmail, setAccountEmail] = useState(email);
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Track the contact form's email until they open the panel, so the common
  // case (type email above, then decide to sign in) needs no retyping. Once
  // they're editing this field it owns itself.
  useEffect(() => {
    if (stage === "collapsed") setAccountEmail(email);
  }, [email, stage]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // The cooldown gates BOTH send buttons, not just "Resend".
  //
  // `resendCooldown` is set from the 429's `retryAfter` as well as after a
  // successful send, and the server's OTP limiter counts a rejected request
  // just the same. Gating only the resend button (which lives in the `otp`
  // stage) left "Send Code" fully enabled at the `email` stage, so a buyer who
  // hit the limit could keep clicking into repeated 429s with the countdown
  // they needed to see sitting one stage away.
  const canSendCode =
    !isLoading && !!accountEmail.trim() && resendCooldown <= 0;

  const requestCode = async (isResend: boolean) => {
    setError(null);
    setIsLoading(true);
    try {
      const result = await sendCustomerOtp(accountEmail.trim().toLowerCase());
      if (!result || result.error || !result.data?.otpToken) {
        setError(result?.error ?? "Failed to send code. Please try again.");
        if (
          result &&
          "retryAfter" in result &&
          typeof result.retryAfter === "number"
        )
          setResendCooldown(result.retryAfter);
        return;
      }
      setOtpToken(result.data.otpToken);
      setOtp("");
      setStage("otp");
      setResendCooldown(60);
      if (isResend) setError(null);
    } catch (err) {
      // A `finally` alone would un-spin the button and say nothing, which reads
      // as "the site ignored me" — the one outcome guaranteed to lose the sale
      // at a step that is optional anyway. Guest checkout is still right there,
      // so surface the failure and let them move on.
      console.error("Could not send the sign-in code", err);
      setError("Something went wrong sending the code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const normalized = accountEmail.trim().toLowerCase();
      const result = await verifyCustomerOtp(normalized, otp.trim(), otpToken);
      if (!result || result.error || !result.data?.authToken) {
        setError(result?.error ?? "Verification failed. Please try again.");
        return;
      }
      const { authToken, customer } = result.data;
      login(authToken, customer);

      // verify-otp returns name and phone but not the address — that lives
      // behind get-customer-profile, which AccountPage also uses to populate
      // session.savedAddress. Fetch it here so a returning buyer doesn't retype
      // an address we already have. Non-fatal: a failure just means they type it.
      //
      // Its own try/catch, because "non-fatal" has to hold for a REJECTION too,
      // not just an `{ error }` body: getCustomerProfile returns `res.json()`
      // from inside its try, so the parse rejection escapes the helper, and a
      // 502 or any non-JSON response would throw straight past the onSignedIn
      // below. The buyer would be signed in with nothing prefilled and no error
      // shown — the failure mode this whole panel exists to avoid.
      let address: SavedAddress | undefined;
      try {
        const profile = await getCustomerProfile(authToken);
        if (profile?.data && !profile.error) {
          address = profile.data.address;
          // Park it on the session too, the way AccountPage does, so a later
          // remount of checkout prefills from session.savedAddress on its own.
          if (address) refreshSession({ savedAddress: address });
        }
      } catch (err) {
        console.error("Could not load the saved address for prefill", err);
      }

      onSignedIn({
        email: customer.email || normalized,
        firstName: customer.firstName ?? "",
        lastName: customer.lastName ?? "",
        phone: customer.phone ?? "",
        address,
      });
      setStage("collapsed");
      setOtp("");
    } catch (err) {
      // Same rule as requestCode: never leave the buyer staring at a button
      // that did nothing. This also catches anything thrown by the login /
      // onSignedIn work above, not just the network call — a prefill that blows
      // up must not look like a failed sign-in with no explanation.
      console.error("Could not verify the sign-in code", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (session) {
    // No `/20` opacity modifier on a themed colour anywhere in this file: every
    // palette entry in tailwind.config.js is a `var(--color-*)`, and Tailwind v3
    // can't split a bare var() into rgb channels, so it drops the utility
    // silently — the class compiles to nothing and the box renders unfilled.
    // Use a solid palette colour instead.
    return (
      <div className="mb-4 rounded-lg border border-accent-light bg-cream-light p-3">
        <p className="text-sm text-brown-dark opensans-text">
          Signed in as <span className="font-semibold">{session.email}</span>.
        </p>
        <p className="text-xs text-gray-600 mt-1">
          Your saved details are filled in below, and any card on file will be
          offered at the payment step.
        </p>
        {/* Checkout FILLS BLANKS ONLY on the account record (see
            upsert-customer.js) — it deliberately never overwrites a detail
            already on file, because on a gift order the "billing" block is the
            recipient's and nothing here can reliably tell. The consequence is
            that a buyer who MOVES can type the new address here, have the
            parcel go to it correctly, and still find the old one prefilled at
            every later checkout: the profile is what feeds the prefill, and
            only the account page rewrites it. Without this link the first
            symptom is a parcel sent to a stale address by someone who didn't
            re-read fields the site had helpfully filled in for them.
            target=_blank so following it can't cost them the checkout. */}
        <p className="text-xs text-gray-600 mt-1">
          Saved details out of date?{" "}
          <a
            href="/account"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brown hover:text-brown-dark underline"
          >
            Update them in your account
          </a>{" "}
          — editing them below changes this order only.
        </p>
        {/* "Not you?" is an identity claim, so signing out has to take the
            identity with it. Everything below this panel — email, name,
            shipping and billing address, phone — was prefilled from the account
            being abandoned, and the buyer who clicks this is telling us it
            isn't theirs. Left in place it checks out silently under the
            previous person's email and ships to their address: the order
            emails land in the wrong inbox. onSignedOut clears it. */}
        <button
          type="button"
          onClick={() => {
            logout();
            onSignedOut?.();
          }}
          className="text-xs text-brown hover:text-brown-dark underline mt-2"
        >
          Not you? Sign out and check out as a guest
        </button>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg border border-accent-light p-3">
      {stage === "collapsed" ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-gray-700 opensans-text">
            Have an account, or want one?{" "}
            <span className="text-gray-500">
              Save your address and card for a one-tap checkout next time.
            </span>
          </p>
          <button
            type="button"
            onClick={() => setStage("email")}
            className="text-sm text-brown hover:text-brown-dark underline whitespace-nowrap"
          >
            Sign in or create an account
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm text-brown-dark opensans-text">
              {stage === "email"
                ? "Enter your email and we'll send a 6-digit code. No password needed — if you don't have an account yet, this creates one."
                : `We sent a 6-digit code to ${accountEmail}.`}
            </p>
            <button
              type="button"
              onClick={() => {
                setStage("collapsed");
                setError(null);
                setOtp("");
              }}
              className="text-xs text-gray-500 hover:text-brown-dark underline whitespace-nowrap"
              aria-label="Continue as a guest without an account"
            >
              Continue as guest
            </button>
          </div>

          {stage === "email" ? (
            <div className="flex flex-col gap-2">
              <label
                htmlFor="checkout-account-email"
                className="text-sm font-semibold text-brown-dark opensans-text"
              >
                Email Address
              </label>
              <input
                id="checkout-account-email"
                type="email"
                autoComplete="email"
                value={accountEmail}
                onChange={(e) => setAccountEmail(e.target.value)}
                // Enter is the reflex after typing an email, and this panel
                // sits inside the checkout <form>: the browser won't implicitly
                // submit it (more than one text field blocks that), so without
                // a handler the key simply does nothing and the panel looks
                // broken. preventDefault anyway — the field count that makes it
                // safe isn't ours to rely on.
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  if (canSendCode) requestCode(false);
                }}
                placeholder="you@example.com"
                className="order-input-field"
              />
              {/* Not a <form>: this panel renders inside the checkout <form>,
                  and a nested form is invalid HTML — Enter here would submit
                  the outer one. Hence a plain button with an explicit handler. */}
              <button
                type="button"
                onClick={() => requestCode(false)}
                disabled={!canSendCode}
                className="btn-primary w-full disabled:opacity-60"
              >
                {isLoading
                  ? "Sending..."
                  : resendCooldown > 0
                    ? `Try again in ${resendCooldown}s`
                    : "Send Code"}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <label
                htmlFor="checkout-account-otp"
                className="text-sm font-semibold text-brown-dark opensans-text"
              >
                Verification Code
              </label>
              <input
                id="checkout-account-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                // Same reason as the email field, more so: a one-time-code
                // field is where people hit Enter without thinking, and iOS
                // autofill drops the code straight in.
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  if (!isLoading && otp.length === 6) handleVerify();
                }}
                placeholder="123456"
                className="order-input-field text-center tracking-[0.5em] text-xl font-mono"
              />
              <button
                type="button"
                onClick={handleVerify}
                disabled={isLoading || otp.length < 6}
                className="btn-primary w-full disabled:opacity-60"
              >
                {isLoading ? "Verifying..." : "Verify & Continue"}
              </button>
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setStage("email");
                    setOtp("");
                    setError(null);
                  }}
                  className="text-brown hover:text-brown-dark underline"
                >
                  ← Change email
                </button>
                <button
                  type="button"
                  onClick={() => requestCode(true)}
                  disabled={resendCooldown > 0 || isLoading}
                  className="text-brown hover:text-brown-dark underline disabled:opacity-50"
                >
                  {resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend code"}
                </button>
              </div>
            </div>
          )}

          {error && (
            <p className="text-rust text-sm" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckoutAccountPanel;
